import type { JSX, KeyboardEvent } from 'react'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Селект. Своя реализация, а НЕ `<select>`.
 *
 * Почему: нативный список отрисовывает операционная система. Он не знает ни про
 * наши цвета, ни про шрифт, ни про радиусы — на тёмной панели это белый
 * системный прямоугольник с чужой типографикой. Прямая жалоба: «дропдауны в
 * селектах не кастомные».
 *
 * Реализовано по паттерну ARIA listbox, а не «див с onClick»:
 *  - кнопка `role="combobox"` с `aria-expanded` / `aria-controls`;
 *  - список `role="listbox"`, элементы `role="option"` с `aria-selected`;
 *  - клавиатура: Enter/Space/стрелки открывают, ↑↓ двигают, Home/End в края,
 *    Enter выбирает, Esc закрывает и возвращает фокус на кнопку, Tab закрывает;
 *  - печать буквами — прыжок к варианту (type-ahead), как в нативном;
 *  - закрытие по клику вне и по скроллу контейнера.
 *
 * Позиционирование: список рисуется рядом с кнопкой и САМ решает, раскрываться
 * вниз или вверх — по месту до нижней кромки окна. Это то, чего не хватало
 * прошлому меню, вылетавшему за карточку.
 */

export interface SelectOption {
  value: string
  label: string
  /** Правая приписка: статус, адрес, версия. */
  note?: string
  disabled?: boolean
}

export interface SelectProps {
  label: string
  value: string
  options: SelectOption[]
  onValueChange: (value: string) => void
  /** Подпись скрыта визуально, но остаётся для скринридера. */
  hideLabel?: boolean
  hint?: string
  error?: string | null
  disabled?: boolean
  placeholder?: string
  className?: string
}

export function Select({
  label,
  value,
  options,
  onValueChange,
  hideLabel = false,
  hint,
  error,
  disabled = false,
  placeholder = 'Выберите',
  className
}: SelectProps): JSX.Element {
  const id = useId()
  const listId = `${id}-list`
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [box, setBox] = useState<ListBox | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const typeAhead = useRef({ query: '', at: 0 })
  /** До этого момента скролл не закрывает список — см. onScroll. */
  const ignoreScrollUntil = useRef(0)

  const selected = options.find(option => option.value === value) ?? null

  /**
   * Куда портировать список.
   *
   * Обычно в `document.body`. Но если селект внутри нативного `<dialog>`, то
   * body не годится: модалка открыта через `showModal()` и живёт в **top layer**,
   * а он рисуется выше всего документа независимо от `z-index`. Список из body
   * оказался бы под диалогом, и никакой слой это не исправит. Внутри самого
   * `<dialog>` он снова наверху — и по-прежнему не обрезается карточками.
   */
  const [portalTo, setPortalTo] = useState<HTMLElement | null>(null)

  // При открытии подсвечиваем выбранный вариант и считаем место под список.
  useLayoutEffect(() => {
    if (!open) return
    ignoreScrollUntil.current = performance.now() + 150
    const index = options.findIndex(option => option.value === value)
    setActiveIndex(index < 0 ? 0 : index)
    setBox(measure(buttonRef.current, options.length))
    setPortalTo(buttonRef.current?.closest('dialog') ?? document.body)
  }, [open, options, value])

  // Размер окна изменился — пересчитываем, а не оставляем список на старом месте.
  useEffect(() => {
    if (!open) return
    const onResize = (): void => setBox(measure(buttonRef.current, options.length))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [open, options.length])

  /**
   * Фокус уводим в список: дальше им управляют стрелки.
   *
   * `preventScroll` обязателен, и это не микрооптимизация. Обычный `focus()`
   * заставляет браузер подкрутить ближайший скроллящийся предок к элементу, а
   * скролл в этом слое закрывает список (см. эффект ниже). Получалась
   * самоубийственная связка: список открывался, сам фокусировался, сам вызывал
   * скролл и сам же от него закрывался. Снаружи это выглядело как «дропдаун
   * мигает и закрывается» — и только там, где кнопка стоит ниже сгиба (мастер
   * создания бота), а в фильтре наверху страницы всё «работало».
   */
  // Зависимость от `box` обязательна: до первого замера списка в DOM ещё нет
  // (он в портале и ждёт геометрию), и фокусировать было бы нечего.
  useEffect(() => {
    if (open && box !== null) listRef.current?.focus({ preventScroll: true })
  }, [open, box])

  // Клик вне и любой скролл снаружи закрывают список. Скролл — потому что
  // список в портале позиционирован по вьюпорту и иначе «отрывается» от кнопки.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target) === true) return
      if (listRef.current?.contains(target) === true) return
      setOpen(false)
    }
    const onScroll = (event: Event): void => {
      if (listRef.current?.contains(event.target as Node) === true) return
      // Страховка на случай, если скролл всё-таки породил не пользователь, а
      // само открытие (браузер может подкрутить контейнер под фокус или под
      // появившийся элемент). Закрываться от собственного побочного эффекта
      // список не должен — один кадр игнорируем.
      if (performance.now() < ignoreScrollUntil.current) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  const commit = (index: number): void => {
    const option = options[index]
    if (option === undefined || option.disabled === true) return
    onValueChange(option.value)
    setOpen(false)
    buttonRef.current?.focus()
  }

  const step = (delta: number): void => {
    setActiveIndex(previous => {
      let next = previous
      for (let guard = 0; guard < options.length; guard++) {
        next = (next + delta + options.length) % options.length
        if (options[next]?.disabled !== true) return next
      }
      return previous
    })
  }

  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        step(1)
        return
      case 'ArrowUp':
        event.preventDefault()
        step(-1)
        return
      case 'Home':
        event.preventDefault()
        setActiveIndex(0)
        return
      case 'End':
        event.preventDefault()
        setActiveIndex(options.length - 1)
        return
      case 'Enter':
      case ' ':
        event.preventDefault()
        commit(activeIndex)
        return
      case 'Escape':
        event.preventDefault()
        setOpen(false)
        buttonRef.current?.focus()
        return
      case 'Tab':
        setOpen(false)
        return
      default:
        break
    }

    // Type-ahead: буквы за последнюю секунду складываются в префикс.
    if (event.key.length !== 1) return
    const now = Date.now()
    const query = (now - typeAhead.current.at < 900 ? typeAhead.current.query : '') + event.key
    typeAhead.current = { query, at: now }
    const found = options.findIndex(
      option =>
        option.disabled !== true && option.label.toLowerCase().startsWith(query.toLowerCase())
    )
    if (found >= 0) setActiveIndex(found)
  }

  const describedBy = [hint === undefined ? null : hintId, error ? errorId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={cn('relative flex min-w-0 flex-col gap-1.5', className)}>
      <label
        htmlFor={id}
        className={cn('text-xs font-medium text-ink-muted', hideLabel && 'sr-only')}
      >
        {label}
      </label>

      <button
        ref={buttonRef}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-haspopup="listbox"
        aria-describedby={describedBy === '' ? undefined : describedBy}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        onClick={() => setOpen(previous => !previous)}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
            event.preventDefault()
            setOpen(true)
          }
        }}
        className={cn(
          'press flex h-9 min-w-0 items-center gap-2 rounded-sm border bg-panel-2 px-3 text-left text-base text-ink',
          'hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-45',
          error ? 'border-fail' : 'border-line',
          open && 'border-honey/60'
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', selected === null && 'text-ink-faint')}>
          {selected?.label ?? placeholder}
        </span>
        {selected?.note === undefined ? null : (
          <span className="shrink-0 font-mono text-2xs text-ink-faint">{selected.note}</span>
        )}
        <ChevronDown
          aria-hidden
          className={cn(
            'size-4 shrink-0 text-ink-faint transition-transform duration-140',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && box !== null && portalTo !== null
        ? createPortal(
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              aria-label={label}
              tabIndex={-1}
              onKeyDown={onListKeyDown}
              style={{
                position: 'fixed',
                left: box.left,
                top: box.top,
                width: box.width,
                maxHeight: box.maxHeight
              }}
              className="anim-pop z-dropdown overflow-y-auto rounded-md border border-line-strong bg-panel-2/95 p-1 shadow-lg backdrop-blur-xl"
            >
              {options.map((option, index) => {
                const isSelected = option.value === value
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled === true || undefined}
                    onPointerEnter={() => setActiveIndex(index)}
                    onClick={() => commit(index)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-xs px-2.5 py-1.5 text-base',
                      option.disabled === true && 'cursor-not-allowed opacity-45',
                      index === activeIndex ? 'bg-panel-3 text-ink' : 'text-ink-muted'
                    )}
                  >
                    <Check
                      aria-hidden
                      className={cn('size-3.5 shrink-0', isSelected ? 'text-honey' : 'opacity-0')}
                    />
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                    {option.note === undefined ? null : (
                      <span className="shrink-0 font-mono text-2xs text-ink-faint">
                        {option.note}
                      </span>
                    )}
                  </li>
                )
              })}
            </ul>,
            portalTo
          )
        : null}

      {hint === undefined ? null : (
        <p id={hintId} className="text-2xs text-ink-muted">
          {hint}
        </p>
      )}
      {error ? (
        <p id={errorId} role="alert" className="text-2xs text-fail">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Геометрия списка в координатах вьюпорта.
 *
 * Список рисуется ПОРТАЛОМ в `document.body`, а не рядом с кнопкой, и это
 * вынужденно: карточки (`Surface`) обрезают содержимое по `overflow: hidden` —
 * иначе не получить скруглённые углы. Абсолютно позиционированный список внутри
 * карточки просто срезался её краем: в мастере создания бота от него оставалась
 * полоска в пару пикселей.
 */
interface ListBox {
  left: number
  top: number
  width: number
  maxHeight: number
}

/** Высота строки варианта и запас на рамку с отступами — для оценки места. */
const OPTION_HEIGHT = 34
const LIST_PADDING = 10
const VIEWPORT_MARGIN = 8
const GAP = 6
const MAX_LIST_HEIGHT = 240

function measure(button: HTMLButtonElement | null, count: number): ListBox | null {
  if (button === null) return null
  const rect = button.getBoundingClientRect()
  const wanted = Math.min(MAX_LIST_HEIGHT, count * OPTION_HEIGHT + LIST_PADDING)
  const below = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN
  const above = rect.top - GAP - VIEWPORT_MARGIN
  // Вверх раскрываемся только когда снизу реально не хватает места, а сверху
  // его больше: иначе список прыгал бы над кнопкой без причины.
  const up = below < wanted && above > below
  const maxHeight = Math.max(96, Math.min(wanted, up ? above : below))
  return {
    left: Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.left, window.innerWidth - rect.width - VIEWPORT_MARGIN)
    ),
    top: up ? rect.top - maxHeight - GAP : rect.bottom + GAP,
    width: rect.width,
    maxHeight
  }
}
